"use client";

import {
  type ColumnDef,
  type PaginationState,
  type RowData,
  type SortingState,
  type TableOptions,
  type TableState,
  columnVisibilityFeature,
  createColumnHelper,
  createPaginatedRowModel,
  createSortedRowModel,
  createTableHook,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_datetime,
  sortFn_text,
  tableFeatures,
} from "@tanstack/react-table";
import { useState } from "react";

// -----------------------------------------------------------------------------
// Features & hook factory
// -----------------------------------------------------------------------------

export const columnIdPrefix = "column";

export const defaultPageSize = 10;

export const defaultPageSizes = [10, 20, 30, 40, 50, 100, 200];

/**
 * Feature set shared by every `DataTable`. Column defs must be authored with
 * `createDataTableColumnHelper` so cell/header render props see these APIs.
 */
export const dataTableFeatures = tableFeatures({
  columnVisibilityFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  paginatedRowModel: createPaginatedRowModel(),
  sortedRowModel: createSortedRowModel(),
  // `text` is what `sortFn: "auto"` resolves to for plain strings; without it
  // v9 silently falls back to case-sensitive `basic`.
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    datetime: sortFn_datetime,
    text: sortFn_text,
  },
});

export type DataTableFeatures = typeof dataTableFeatures;

const { useAppTable, useTableContext } = createTableHook({
  features: dataTableFeatures,
  // Opt-in: selecting tables must also render a checkbox/switch column.
  enableRowSelection: false,
});

export function createDataTableColumnHelper<TData extends RowData>() {
  return createColumnHelper<DataTableFeatures, TData>();
}

/**
 * Reads the nearest `DataTable` instance. Available to anything rendered by
 * `DataTable` (cells, headers, `render*` props, and `DataTable*` subcomponents).
 */
export const useDataTableContext = useTableContext;

export type DataTableOptions<TData extends RowData> = Omit<
  TableOptions<DataTableFeatures, TData>,
  "data" | "columns" | "features" | "initialState"
> & {
  initialState?:
    | (Omit<Partial<TableState<DataTableFeatures>>, "pagination"> & {
        pagination?: Partial<PaginationState> | undefined;
      })
    | undefined;
};

export interface UseDataTableParams<TData extends RowData> {
  // `any` mirrors TanStack's own `columnHelper.columns()` — TValue is invariant in v9.
  columns: ReadonlyArray<ColumnDef<DataTableFeatures, TData, any>>;
  data: TData[];
  options?: DataTableOptions<TData> | undefined;
}

export function useDataTable<TData extends RowData>({
  columns,
  data,
  options = {},
}: UseDataTableParams<TData>) {
  // eslint-disable-next-line react-hooks/incompatible-library
  return useAppTable({
    columns,
    data,
    ...options,
    initialState: {
      ...options.initialState,
      pagination: {
        pageIndex: 0,
        pageSize: defaultPageSize,
        ...options.initialState?.pagination,
      },
    },
  });
}

export type DataTableInstance<TData extends RowData> = ReturnType<
  typeof useDataTable<TData>
>;

/**
 * Whether the default footer (row-selection status and/or pagination) has
 * anything to show. `DataTable` uses this for both rendering and styling.
 */
export function getDataTableFooterVisibility<TData extends RowData>(
  table: DataTableInstance<TData>,
) {
  const isRowSelectionEnabled =
    typeof table.options.enableRowSelection === "function" ||
    table.options.enableRowSelection === true;
  const isRowSelectionVisible =
    isRowSelectionEnabled && table.getRowCount() > 0;
  const isPaginationVisible = table.getPageCount() > 1;
  return { isRowSelectionVisible, isPaginationVisible };
}

// -----------------------------------------------------------------------------
// Sorting form (page-owned controlled sorting + `DataTableSortingDialog`)
// -----------------------------------------------------------------------------

export type SortingDirection = "asc" | "desc";

export type SortingFormOptions<TSortingKey extends string> = Array<{
  label: string;
  value: `${TSortingKey}-${SortingDirection}`;
}>;

function formatSortingState(value: string): SortingState {
  const [sortingKey, sortingDirection] = value.split("-") as [string, string];
  const id = `${columnIdPrefix}-${sortingKey}`;
  const desc = sortingDirection === "desc";
  return [{ id, desc }];
}

function parseSortingState(value: SortingState) {
  const sortingKey = value[0]!.id.replace(`${columnIdPrefix}-`, "");
  const sortingDirection = value[0]!.desc ? "desc" : "asc";
  return `${sortingKey}-${sortingDirection}`;
}

export function useDataTableSortingForm(initialState: SortingState) {
  const [sorting, setSorting] = useState(initialState);

  const defaultSortingFormValue = parseSortingState(
    sorting.length > 0 ? sorting : initialState,
  );

  const resetSorting = () => {
    setSorting(initialState);
  };

  const submitSorting: React.SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const fieldValue = formData.get(columnIdPrefix) as string;
    const nextState = formatSortingState(fieldValue);
    setSorting(nextState);
  };

  return {
    defaultSortingFormValue,
    resetSorting,
    setSorting,
    sorting,
    submitSorting,
  };
}
